import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../firebase-client';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { buildMirrorCalendarEvents } from '../../utils/calendarMirrorEvents';
import { Card } from '../ui/card';
import './dashboardEventTicketCard.css';

const TICKET_WINDOW_BEFORE_MS = 3 * 60 * 60 * 1000;
const TICKET_WINDOW_AFTER_MS = 60 * 60 * 1000;
const REFRESH_INTERVAL_MS = 60 * 1000;

function baseEventId(details = {}) {
  return String(details.originalEventId || details.eventId || '').split('__')[0];
}

function courseIdFromDetails(details = {}) {
  const explicitCourseId = String(details.courseId || details.genericCourseId || '').trim();
  if (explicitCourseId) return explicitCourseId;
  const mirrorId = String(details.originalEventId || details.eventId || '');
  const match = mirrorId.match(/^course__(.+?)__(.+)$/);
  return match?.[1] || '';
}

export function activeDashboardTickets(rows, now = new Date()) {
  const nowMs = now.getTime();
  // Looking back by the post-start window keeps the current occurrence of a
  // recurring series available instead of advancing immediately to next week.
  const recurrenceScanTime = new Date(nowMs - TICKET_WINDOW_AFTER_MS - 1);
  const candidates = buildMirrorCalendarEvents(rows, {
    now: recurrenceScanTime,
    maxOccurrences: 2,
    seriesMode: 'next',
  }).flatMap(event => {
    const details = event.details || {};
    const start = event.start instanceof Date ? event.start : new Date(event.start);
    const courseId = courseIdFromDetails(details);
    const isCourseSession = details.attendanceMode === 'course'
      && details.courseStatus === 'enrolled'
      && Boolean(courseId);
    if (isCourseSession) {
      // Course mirrors carry an ISO start. Prefer it over the calendar's local
      // date/time projection so the arrival prompt remains DST-safe.
      const courseStart = new Date(details.start || start);
      const end = new Date(courseStart.getTime() + Math.max(1, Number(details.durationMinutes || 60)) * 60000);
      if (
        !details.teamId
        || Number.isNaN(courseStart.getTime())
        || Number.isNaN(end.getTime())
        || nowMs < courseStart.getTime() - TICKET_WINDOW_BEFORE_MS
        || nowMs >= end.getTime() + TICKET_WINDOW_AFTER_MS
      ) return [];
      return [{
        sourceType: 'course',
        teamId: details.teamId,
        courseId,
        eventName: event.title || details.name || 'Course session',
        startsAt: courseStart,
        walletPath: details.ticketWalletPath
          || `/tickets/${encodeURIComponent(details.teamId)}/course/${encodeURIComponent(courseId)}`,
      }];
    }
    const eventId = baseEventId(details);
    if (
      details.attendanceMode !== 'ticketed'
      || details.ticketStatus !== 'paid'
      || !details.teamId
      || !eventId
      || Number.isNaN(start.getTime())
      || nowMs < start.getTime() - TICKET_WINDOW_BEFORE_MS
      || nowMs >= start.getTime() + TICKET_WINDOW_AFTER_MS
    ) return [];
    return [{
      sourceType: 'event',
      teamId: details.teamId,
      eventId,
      eventName: event.title || details.name || 'Event',
      startsAt: start,
      walletPath: `/tickets/${encodeURIComponent(details.teamId)}/${encodeURIComponent(eventId)}`,
    }];
  }).sort((left, right) => left.startsAt - right.startsAt);
  const uniqueTickets = new Map();
  candidates.forEach(ticket => {
    const sourceId = ticket.sourceType === 'course' ? ticket.courseId : ticket.eventId;
    const key = `${ticket.sourceType}:${ticket.teamId}:${sourceId}`;
    if (!uniqueTickets.has(key)) uniqueTickets.set(key, ticket);
  });
  return Array.from(uniqueTickets.values());
}

function timingLabel(startsAt, now) {
  const difference = startsAt.getTime() - now.getTime();
  if (difference <= 0) return 'Happening now';
  const minutes = Math.max(1, Math.ceil(difference / 60000));
  if (minutes < 60) return `Starts in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `Starts in ${hours} hr${remainder ? ` ${remainder} min` : ''}`;
}

export default function DashboardEventTicketCard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!user?.uid) {
      setRows([]);
      return undefined;
    }
    return safeOnSnapshot(collection(db, `privateUsers/${user.uid}/calendar`), snapshot => {
      setRows(snapshot.docs.map(docSnapshot => ({ id: docSnapshot.id, data: docSnapshot.data() || {} })));
      setNow(new Date());
    }, undefined, { label: 'event-ticket-calendar' });
  }, [user?.uid]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, []);

  const tickets = useMemo(() => activeDashboardTickets(rows, now), [now, rows]);
  if (!tickets.length) return null;
  const ticketTypes = new Set(tickets.map(ticket => ticket.sourceType));
  const heading = ticketTypes.size > 1
    ? 'Your tickets'
    : ticketTypes.has('course') ? 'Your course tickets' : 'Your event tickets';

  return <Card className="dashboard-card dashboard-ticket-card" aria-labelledby="dashboard-ticket-title">
    <div className="dashboard-ticket-card__header">
      <div>
        <p className="dashboard-ticket-card__eyebrow">Ready for arrival</p>
        <h2 id="dashboard-ticket-title">{heading}</h2>
      </div>
      <span className="dashboard-ticket-card__status">Ready</span>
    </div>
    <div className="dashboard-ticket-card__events">
      {tickets.map(ticket => <article className="dashboard-ticket-card__event" key={`${ticket.sourceType}:${ticket.teamId}:${ticket.courseId || ticket.eventId}`}>
        <div className="dashboard-ticket-card__details">
          <h3>{ticket.eventName}</h3>
          <div className="dashboard-ticket-card__time">
            <span>{new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(ticket.startsAt)}</span>
            <span aria-hidden="true">·</span>
            <strong>{timingLabel(ticket.startsAt, now)}</strong>
          </div>
        </div>
        <Link className="button button-primary dashboard-ticket-card__action" to={ticket.walletPath}>Show tickets</Link>
      </article>)}
    </div>
  </Card>;
}
